import { ASSETS, LINKS } from "@/lib/brand";
import { ArrowUpRight, Instagram } from "lucide-react";

export const PrimoGroup = () => {
  return (
    <section id="real-estate" data-testid="primo-section" className="relative py-24 md:py-32 bg-cream">
      {/* Subtle background luxe image */}
      <div
        className="absolute inset-0 opacity-[0.08] bg-cover bg-center pointer-events-none"
        style={{ backgroundImage: `url(${ASSETS.interiorBg})` }}
      />
      <div className="container-edit relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-end">
          <div className="md:col-span-7">
            <div className="overline mb-5">— 02 / Real Estate</div>
            <h2 className="font-serif-heading text-4xl md:text-6xl text-charcoal leading-[1]" data-testid="primo-title">
              Primo Group.<br />
              <span className="italic text-bronze-deep">Powered by Valery Real Estate Inc.</span>
            </h2>
            <p className="mt-6 max-w-xl text-ink text-base md:text-lg leading-relaxed">
              A boutique real estate team built on trust, clarity, and results.
              Stefanie Winstan &amp; Jonathan Regali serve buyers, sellers, and
              investors across Ottawa &amp; surrounding areas.
            </p>
          </div>
          <div className="md:col-span-5 flex md:justify-end" data-testid="primo-logo-wrap">
            <img
              src={ASSETS.primoLogo}
              alt="Primo Group — Powered by Valery Real Estate Inc. Brokerage"
              className="h-44 md:h-56 lg:h-64 w-auto object-contain"
              data-testid="primo-logo-img"
            />
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-stretch">
          <div className="md:col-span-7 relative border border-charcoal/10 bg-ivory" data-testid="primo-team-card">
            <img
              src={ASSETS.team}
              alt="Stefanie Winstan and Jonathan Regali"
              className="w-full h-[380px] md:h-[520px] object-cover"
              data-testid="primo-team-img"
            />
            <div className="p-6 md:p-8 border-t border-charcoal/10">
              <div className="overline">The Team</div>
              <h3 className="font-serif-heading text-xl md:text-2xl text-charcoal mt-2 leading-snug">
                Stefanie Winstan <span className="text-charcoal/50">·</span> Jonathan Regali
              </h3>
              <p className="text-ink text-xs md:text-sm mt-1">Co-founders · Realtors · Ottawa &amp; Surrounding Areas</p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <a
                  href={LINKS.primoInstagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-charcoal border border-charcoal/30 px-3 py-2 hover:bg-charcoal hover:text-ivory hover:border-charcoal transition-colors"
                  data-testid="primo-instagram-link"
                  aria-label="Primo Group on Instagram"
                >
                  <Instagram size={12} strokeWidth={1.6} /> primogroupottawa
                </a>
                <a
                  href={LINKS.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-charcoal border-b border-charcoal pb-1 hover:text-bronze hover:border-bronze transition-colors"
                  data-testid="primo-website-link"
                >
                  thepgsells.com <ArrowUpRight size={12} strokeWidth={1.5} />
                </a>
              </div>
            </div>
          </div>

          <aside className="md:col-span-5 flex flex-col gap-6">
            <div className="card-edit p-8 md:p-10">
              <div className="overline">Buyers</div>
              <h4 className="font-serif-heading text-2xl text-charcoal mt-2">
                From first keys to forever homes.
              </h4>
              <p className="text-ink text-sm mt-3">
                Guidance through pre-approval, showings, offers, and closing — with
                a team that actually answers the phone.
              </p>
            </div>
            <div className="card-edit p-8 md:p-10">
              <div className="overline">Sellers</div>
              <h4 className="font-serif-heading text-2xl text-charcoal mt-2">
                Priced right. Marketed beautifully.
              </h4>
              <p className="text-ink text-sm mt-3">
                Professional staging, cinematic listings, and pricing strategy that
                respects the market.
              </p>
            </div>
            <a
              href={ASSETS.homeValueUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary w-full"
              data-testid="primo-cta-valuation"
            >
              What's Your Home Worth?
            </a>
          </aside>
        </div>

        {/* Neighbourhoods */}
        <div className="mt-16 md:mt-20 border-t border-charcoal/10 pt-10" data-testid="primo-neighbourhoods">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <div className="overline">Where we work</div>
              <h3 className="font-serif-heading text-3xl md:text-4xl text-charcoal mt-2">
                Neighbourhoods we know <span className="italic text-bronze-deep">by heart.</span>
              </h3>
            </div>
            <p className="text-ink text-sm max-w-sm">
              From quiet family streets to vibrant city pockets — these are the
              communities Primo Group serves across Ottawa &amp; surrounding areas.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-charcoal/10 border border-charcoal/10">
            {[
              "Stittsville",
              "Kanata",
              "Kanata Lakes",
              "Bridlewood",
              "Richmond",
              "Westboro",
              "Hintonburg",
              "Nepean",
              "Carleton Place",
              "Kemptville",
            ].map((n) => (
              <div
                key={n}
                data-testid={`neighbourhood-${n.toLowerCase().replace(/\s/g, "-")}`}
                className="bg-ivory px-5 py-6 md:py-8 group hover:bg-cream transition-colors"
              >
                <div className="overline">— Ottawa</div>
                <div className="font-serif-heading text-xl md:text-2xl text-charcoal mt-1 group-hover:text-bronze-deep transition-colors">
                  {n}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-mute mt-6">
            Don&apos;t see your neighbourhood? <a href="#contact" className="underline-offset-2 hover:underline hover:text-bronze">Send a message</a> — we likely cover it too.
          </p>
        </div>
      </div>
    </section>
  );
};

export default PrimoGroup;
